import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Unlock, Lock, Key, AlertCircle } from "lucide-react";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UnlockPdf() {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [unlockedBlob, setUnlockedBlob] = useState(null);
  const [isLocked, setIsLocked] = useState(null); // null = unknown, true = locked, false = not locked
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Unlock PDF - Remove PDF Password - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setUnlockedBlob(null);
      setPassword("");
      setError("");
      setIsLocked(null);
      await checkIfLocked(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setUnlockedBlob(null);
      setPassword("");
      setError("");
      setIsLocked(null);
      await checkIfLocked(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const checkIfLocked = async (pdfFile) => {
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const response = await fetch(`${API_URL}/api/check-pdf-encryption`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsLocked(data.is_encrypted);
        if (data.is_encrypted) {
          toast.info("This PDF is password protected. Enter the password to unlock.");
        } else {
          toast.info("This PDF is not password protected, but you can still process it.");
        }
      }
    } catch (error) {
      console.error("Error checking PDF:", error);
      // Assume it might be locked if we can't check
      setIsLocked(true);
    }
  };

  const unlockPdf = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError("");

    try {
      setProgress(20);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('password', password);
      
      setProgress(40);
      
      const response = await fetch(`${API_URL}/api/unlock-pdf`, {
        method: 'POST',
        body: formData,
      });
      
      setProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unlock PDF');
      }

      const blob = await response.blob();
      setUnlockedBlob(blob);
      setProgress(100);

      toast.success("PDF unlocked successfully!");
    } catch (error) {
      console.error("Unlock error:", error);
      setError(error.message);
      if (error.message.toLowerCase().includes('password')) {
        toast.error("Incorrect password or password required");
      } else {
        toast.error("Failed to unlock PDF: " + error.message);
      }
    }

    setIsProcessing(false);
  };

  const downloadUnlocked = () => {
    if (!unlockedBlob) return;
    const url = URL.createObjectURL(unlockedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace('.pdf', '_unlocked.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Unlocked PDF downloaded!");
  };

  const clearFile = () => {
    setFile(null);
    setUnlockedBlob(null);
    setPassword("");
    setIsLocked(null);
    setError("");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Unlock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> Secure
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full mb-4">
            <Unlock className="w-5 h-5" />
            <span className="font-medium">Unlock PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Unlock PDF - Remove Password
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Remove password protection from your PDF files. Enter the password if required.
          </p>
        </div>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
            isDragging ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {!file ? (
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a password-protected PDF file
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {isLocked ? (
                    <Lock className="w-16 h-16 text-amber-500" />
                  ) : (
                    <FileText className="w-16 h-16 text-amber-500" />
                  )}
                </div>
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  {file.name}
                </h3>
                <div className="flex items-center justify-center gap-2 mb-4">
                  {isLocked !== null && (
                    <Badge variant={isLocked ? "destructive" : "secondary"}>
                      {isLocked ? "Password Protected" : "Not Protected"}
                    </Badge>
                  )}
                  <span className="text-slate-500 dark:text-slate-400">
                    {formatSize(file.size)}
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={clearFile}>
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {file && !unlockedBlob && (
          <div className="max-w-2xl mx-auto mt-6 space-y-6">
            {/* Password Input */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <Label className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4" /> PDF Password
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter PDF password (if required)..."
                  className="mb-2"
                  data-testid="password-input"
                />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isLocked 
                    ? "Enter the password to unlock this PDF" 
                    : "Leave empty if the PDF doesn't require a password"}
                </p>
              </CardContent>
            </Card>

            {/* Error Message */}
            {error && (
              <Card className="dark:bg-slate-800 border-red-200 dark:border-red-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Unlock Failed
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                        {error}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {isProcessing && (
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Unlocking PDF...
                    </span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </CardContent>
              </Card>
            )}

            {!isProcessing && (
              <div className="text-center">
                <Button 
                  onClick={unlockPdf}
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90"
                  data-testid="unlock-btn"
                >
                  <Unlock className="w-5 h-5 mr-2" />
                  Unlock PDF
                </Button>
              </div>
            )}
          </div>
        )}

        {unlockedBlob && (
          <div className="max-w-2xl mx-auto mt-6">
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Unlock className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white mb-2">
                  PDF Unlocked Successfully!
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-2">
                  Password protection has been removed from your PDF.
                </p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mb-4">
                  Size: {formatSize(unlockedBlob.size)}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={downloadUnlocked}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:opacity-90"
                    data-testid="download-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Unlocked PDF
                  </Button>
                  <Button variant="outline" onClick={clearFile}>
                    Unlock Another PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <div className="max-w-2xl mx-auto mt-6">
          <Card className="dark:bg-slate-800 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" /> How it works
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                <li>• Upload your password-protected PDF</li>
                <li>• Enter the correct password (you must know the password)</li>
                <li>• Download the unlocked version without password protection</li>
                <li>• The unlocked PDF can be opened by anyone without a password</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Your files are processed securely.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default UnlockPdf;
